import type {
  AutocompleteData,
  ColoredMaterialAutocompleteProperties,
} from "@color-finder/color-finder-api/models";
import type { AxiosRequestConfig } from "axios";
import { paramCase } from "change-case";
import type { ColorSearchSettings, Dataset } from "models/colors";
import { useDataset } from "providers/DatasetProvider";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { appendDatasetParam } from "service/utils/dataset";
import { filtersToParams } from "./colored-materials";
import type { FilterEntry } from "./filters";
import { type RequestState, useAuthenticatedRequest } from "./request";

type AutocompleteEntityType =
  | ColoredMaterialAutocompleteProperties
  | "industry"
  | "applicationClass"
  | "application";

export function useAutocompleteOptions(
  entityType: AutocompleteEntityType,
  query: string | undefined,
  limit: number | undefined,
  shouldLoad?: boolean,
  filters?: Array<FilterEntry>,
): RequestState<Array<AutocompleteData>> {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { dataset } = useDataset();

  const config = useMemo(
    () =>
      createRequestConfig({
        query,
        limit,
        filters,
        settings,
        entityType,
        dataset,
      }),
    [query, limit, filters, settings, entityType, dataset],
  );

  return useAuthenticatedRequest<Array<AutocompleteData>>(
    `/v0/color-search/autocomplete/${paramCase(entityType)}`,
    config,
    shouldLoad,
  );
}

interface PaginatedAutocompleteInput {
  entityType: AutocompleteEntityType;
  query: string | undefined;
  pageSize: number;
  shouldLoad?: boolean;
  filters?: Array<FilterEntry>;
}

interface PaginatedAutocompleteResult {
  options: Array<AutocompleteData>;
  error: Error | undefined;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => boolean;
  retry: () => void;
}

interface PaginationState {
  requestInput: RequestConfigInput;
  // a new object per page request, so a retry changes the config identity at the same offset
  pageRequest: { offset: number };
  pages: Array<Array<AutocompleteData>>;
  hasMore: boolean;
}

export function usePaginatedAutocompleteOptions({
  entityType,
  query,
  pageSize,
  shouldLoad,
  filters,
}: PaginatedAutocompleteInput): PaginatedAutocompleteResult {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { dataset } = useDataset();

  const requestInput = useMemo<RequestConfigInput>(
    () => ({
      query,
      limit: pageSize + 1,
      filters,
      settings,
      entityType,
      dataset,
    }),
    [query, pageSize, filters, settings, entityType, dataset],
  );

  const [state, setState] = useState<PaginationState>(() =>
    createInitialState(requestInput),
  );
  const current = useMemo<PaginationState>(
    () =>
      state.requestInput === requestInput
        ? state
        : createInitialState(requestInput),
    [state, requestInput],
  );

  const config = useMemo(
    () =>
      createRequestConfig({
        ...current.requestInput,
        offset: current.pageRequest.offset,
      }),
    [current.requestInput, current.pageRequest],
  );

  const [result, error, isLoading] = useAuthenticatedRequest<
    Array<AutocompleteData>
  >(
    `/v0/color-search/autocomplete/${paramCase(entityType)}`,
    config,
    shouldLoad,
  );

  // useRequest keeps the previous result for one render after the config changes.
  const handledResult = useRef<Array<AutocompleteData>>();
  useEffect(() => {
    if (!result || handledResult.current === result) {
      return;
    }
    handledResult.current = result;
    const pages = current.pages.slice(0, current.pageRequest.offset / pageSize);
    pages.push(result.slice(0, pageSize));
    setState({ ...current, pages, hasMore: result.length > pageSize });
  }, [result, current, pageSize]);

  const { pages, hasMore } = current;
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) {
      return false;
    }
    setState({
      ...current,
      pageRequest: { offset: pages.length * pageSize },
    });
    return true;
  }, [isLoading, hasMore, current, pages.length, pageSize]);

  const retry = useCallback(() => {
    setState({
      ...current,
      pageRequest: { offset: current.pageRequest.offset },
    });
  }, [current]);

  const options = useMemo(() => pages.flat(), [pages]);

  return { options, error, isLoading, hasMore, loadMore, retry };
}

function createInitialState(requestInput: RequestConfigInput): PaginationState {
  return {
    requestInput,
    pageRequest: { offset: 0 },
    pages: [],
    hasMore: false,
  };
}

interface RequestConfigInput {
  query: string | undefined;
  limit: number | undefined;
  offset?: number;
  filters?: Array<FilterEntry>;
  settings?: ColorSearchSettings;
  entityType?: string;
  dataset: Dataset;
}

interface SettingsParamsInput {
  params: URLSearchParams;
  entityType?: string;
  settings?: ColorSearchSettings;
}

/** Narrows the year / preliminary color code lists by the saved user settings. */
function appendSettingsParams({
  params,
  entityType,
  settings,
}: SettingsParamsInput) {
  if (
    entityType === "yearOfDevelopment" &&
    settings?.preliminaryColorCode &&
    settings?.preliminaryColorCode.length
  ) {
    settings?.preliminaryColorCode.sort().forEach((c) => {
      params.append("preliminaryColorCode", c.value);
    });
  }

  if (entityType === "preliminaryColorCode" && settings) {
    console.log("settings: ", settings);
    if (settings?.brand && settings?.brand.length > 0) {
      settings?.brand.sort().forEach((f) => {
        params.append("brand", f.value);
      });
    }
    if (settings?.cdc && settings?.cdc.length > 0) {
      settings?.cdc.sort().forEach((f) => {
        params.append("cdc", f.value);
      });
    }
    if (settings?.yearOfDevelopment && settings?.yearOfDevelopment.length > 0) {
      params.set("yearOfDevelopment", settings?.yearOfDevelopment);
    }
  }
}

function createRequestConfig({
  query,
  limit,
  filters,
  settings,
  entityType,
  dataset,
  offset,
}: RequestConfigInput): AxiosRequestConfig {
  const config = filters
    ? filtersToParams(
        filters,
        undefined,
        undefined,
        undefined,
        settings?.externalViewVisible,
      )
    : { params: new URLSearchParams() };
  if (settings?.externalViewVisible) {
    config.params.set("isExternalView", String(true));
  }
  appendDatasetParam(config.params, dataset);

  appendSettingsParams({ params: config.params, entityType, settings });

  if (query) {
    config.params.set("query", query);
  }

  if (limit) {
    config.params.set("limit", limit.toString());
  }

  if (offset && offset > 0) {
    config.params.set("offset", offset.toString());
  }
  return config;
}
